import { FC, useCallback } from 'react';
import { useMutation } from 'react-relay';
import { Form, Formik } from 'formik';
import { object, ref, string } from 'yup';
import graphql from 'babel-plugin-relay/macro';

import Stack from '@mui/material/Stack';
import Page from 'components/Page';
import TextField from 'components/TextField';
import Button from 'components/Button';

import { useNotification } from 'components/Notifications';
import { AccountPasswordPageMutation } from '__generated__/AccountPasswordPageMutation.graphql';

type FormFields = {
  current: string;
  new: string;
  confirm: string;
};

const initial = {
  current: '',
  new: '',
  confirm: '',
};

const validation = object().shape({
  current: string().required('Prašome įvesti dabartinį slaptažodį'),
  new: string().required('Prašome įvesti naują slaptažodį'),
  confirm: string()
    .oneOf([ref('new')], 'Nesutampa slaptažodžiai')
    .required('Pakartokite slaptažodį'),
});

const AccountPasswordPage: FC = () => {
  const { pop } = useNotification();

  const [commit, loading] = useMutation<AccountPasswordPageMutation>(graphql`
    mutation AccountPasswordPageMutation($current: String!, $new: String!) {
      changePassword(current: $current, new: $new)
    }
  `);

  const handleSubmit = useCallback(
    (values: FormFields) => {
      commit({
        variables: {
          current: values.current,
          new: values.new,
        },
        onCompleted: (response, errors) => {
          if (errors) {
            pop(errors[0]?.message ?? 'Nepavyko išsaugoti');
            return;
          }

          if (response.changePassword) {
            pop('Slaptažodis pakeistas sėkmingai');
          }
        },
      });
    },
    [commit, pop]
  );

  return (
    <Page breadcrumbs={[{ name: 'Slaptažodžio keitimas' }]}>
      <Formik<FormFields>
        initialValues={initial}
        onSubmit={handleSubmit}
        validationSchema={validation}>
        <Form>
          <Stack spacing={4}>
            <TextField
              name="current"
              label="Dabartinis slaptažodis"
              type="password"
            />
            <TextField name="new" label="Naujas slaptažodis" type="password" />
            <TextField name="confirm" label="Pakartokite" type="password" />

            <Button loading={loading} type="submit">
              Saugoti
            </Button>
          </Stack>
        </Form>
      </Formik>
    </Page>
  );
};

export default AccountPasswordPage;
